import { LAYER_NORM_NONE } from '../../constants/layers';
import { Mesh } from '../../types/mesh';
import { polyAreaWithHoles, polyPerimWithHoles } from './cadUtils';
import { CadEditorState, CadPolygon, CadScaleRatio } from './types';

/** File name of the 3D model built from a drawing (at project creation and on every update). */
export const CAD_MODEL_FILE_NAME = 'cad-project.glb';

type StoredPolygon = Omit<CadPolygon, 'area' | 'perimeter'>;

/**
 * A drawing's document, as the backend stores it (drawings, and the snapshot a project's model was
 * built from). It holds what the editor needs to reopen the plan exactly as it was left: polygons in
 * canvas units, the scale calibration, the reference image placement and the model origin.
 */
export interface DrawingDocument {
  schemaVersion: 1;
  calibration: {
    /** Meters per canvas unit. */
    metersPerUnit: number;
    scaleRatio: CadScaleRatio | null;
    scaleSet: boolean;
  };
  /** Reference plan image placement in canvas units (its bottom-left corner sits at 0,0). */
  reference: { imageScale: number } | null;
  /**
   * Meters subtracted from the scaled points to get model coordinates. Fixed when the project is
   * created, so later edits keep the model, and the luminaires placed on it, where they were.
   */
  origin: [number, number];
  polygons: StoredPolygon[];
  /** GLB node name of each polygon (by id) in the model built from this drawing. */
  meshNames?: Record<string, string>;
  /**
   * The CAD file (DWG/DXF) the drawing is traced from, shown under the polygons; the file itself is
   * stored with the drawing. `layers`: the polygons created from each of its layers.
   */
  cad?: DrawingCad | null;
}

export interface DrawingCad {
  fileName: string;
  layers: Record<string, string[]>;
}

export interface LoadedDrawing {
  polygons: CadPolygon[];
  metersPerUnit: number;
  scaleRatio: CadScaleRatio | null;
  scaleSet: boolean;
  imageScale: number | null;
  origin: [number, number];
  meshNames: Record<string, string>;
  cad: DrawingCad | null;
}

const withMeasures = (polygon: StoredPolygon): CadPolygon => ({
  ...polygon,
  area: polyAreaWithHoles(polygon.points, polygon.holes),
  perimeter: polyPerimWithHoles(polygon.points, polygon.holes),
});

const stripMeasures = ({ area: _area, perimeter: _perimeter, ...polygon }: CadPolygon): StoredPolygon => polygon;

export function toDrawingDocument(
  state: Pick<CadEditorState, 'polygons' | 'metersPerUnit' | 'scaleRatio' | 'scaleSet'>,
  options: {
    imageScale: number | null;
    origin: [number, number];
    meshNames?: Record<string, string>;
    cad?: DrawingCad | null;
  },
): DrawingDocument {
  return {
    schemaVersion: 1,
    calibration: { metersPerUnit: state.metersPerUnit, scaleRatio: state.scaleRatio, scaleSet: state.scaleSet },
    reference: options.imageScale !== null ? { imageScale: options.imageScale } : null,
    origin: options.origin,
    polygons: state.polygons.map(stripMeasures),
    ...(options.meshNames ? { meshNames: options.meshNames } : {}),
    ...(options.cad ? { cad: options.cad } : {}),
  };
}

/** Reads a stored drawing; null when it is not a drawing this editor understands. */
export function fromDrawingDocument(value: unknown): LoadedDrawing | null {
  const doc = value as Partial<DrawingDocument> | null;
  if (!doc || doc.schemaVersion !== 1 || !Array.isArray(doc.polygons) || !doc.calibration) return null;
  const metersPerUnit = Number(doc.calibration.metersPerUnit);
  if (!Number.isFinite(metersPerUnit) || metersPerUnit <= 0) return null;
  return {
    polygons: doc.polygons.map(withMeasures),
    metersPerUnit,
    scaleRatio: doc.calibration.scaleRatio ?? null,
    scaleSet: doc.calibration.scaleSet ?? true,
    imageScale: doc.reference?.imageScale ?? null,
    origin: Array.isArray(doc.origin) && doc.origin.length === 2 ? [doc.origin[0], doc.origin[1]] : [0, 0],
    meshNames: doc.meshNames ?? {},
    cad: doc.cad?.fileName ? { fileName: doc.cad.fileName, layers: doc.cad.layers ?? {} } : null,
  };
}

const toModel = ([x, y]: [number, number], metersPerUnit: number, origin: [number, number]): [number, number] => [
  x * metersPerUnit - origin[0],
  y * metersPerUnit - origin[1],
];

/** Polygons in model coordinates (meters, minus the origin): what the GLB is built from. */
export function toModelPolygons(polygons: CadPolygon[], metersPerUnit: number, origin: [number, number]): CadPolygon[] {
  return polygons.map((polygon) => ({
    ...polygon,
    points: polygon.points.map((point) => toModel(point, metersPerUnit, origin)),
    holes: polygon.holes?.map((hole) => hole.map((point) => toModel(point, metersPerUnit, origin))),
  }));
}

/**
 * A model-space point (workspace X/Y, where luminaires are placed) back in canvas units. The
 * workspace shows the Y-up GLB rotated so that model (x, y) is canvas point (x, y) scaled and shifted.
 */
export function modelToCanvas(
  [x, y]: [number, number],
  metersPerUnit: number,
  origin: [number, number],
): [number, number] {
  return [(x + origin[0]) / metersPerUnit, (y + origin[1]) / metersPerUnit];
}

/**
 * Origin for a new project: the reference image centre when there is one (the model lines up with
 * the plan image), otherwise the centre of the drawing's bounding box.
 */
export function computeOrigin(
  polygons: CadPolygon[],
  metersPerUnit: number,
  imageCenter: [number, number] | null,
): [number, number] {
  if (imageCenter) return imageCenter;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const polygon of polygons) {
    if (polygon.points.length < 3) continue;
    for (const [x, y] of polygon.points) {
      minX = Math.min(minX, x * metersPerUnit);
      maxX = Math.max(maxX, x * metersPerUnit);
      minY = Math.min(minY, y * metersPerUnit);
      maxY = Math.max(maxY, y * metersPerUnit);
    }
  }
  if (!Number.isFinite(minX)) return [0, 0];
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

/**
 * The scene layer of each polygon. `name` must match the GLB node name (taman-ml pairs geometry with
 * layer settings by it); `uuid` is the polygon id, which ties a layer to its polygon across edits.
 */
export function polygonsToMeshes(polygons: CadPolygon[], meshNames: string[]): Mesh[] {
  return polygons.map((polygon, index) => ({
    uuid: polygon.id,
    name: meshNames[index],
    displayName: polygon.name,
    color: polygon.color,
    type: polygon.type.toUpperCase(),
    normName: polygon.illuminated && polygon.norm !== LAYER_NORM_NONE ? polygon.norm : undefined,
    canHoldLight: polygon.canHoldLight,
    desiredLuminaireHeight: polygon.desiredLuminaireHeight,
    isIlluminated: polygon.illuminated,
  }));
}
